import React, { useEffect, useRef, useState } from 'react';
import { X, Download, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import * as pdfjsLib from 'pdfjs-dist';
import { useLanguage } from './LanguageContext';

// Load the PDF.js worker from our own origin (public/pdf.worker.min.mjs) instead of a CDN.
// cdnjs is not a 1:1 npm mirror and did not serve this exact version, which broke the viewer.
// The worker is copied from node_modules into public/ (see scripts/copy-pdf-worker.js, runs on install).
pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.mjs`;

interface DocumentViewerModalProps {
  blob: Blob | null;
  fileName: string;
  contentType: string;
  onClose: () => void;
  onDownload: () => void;
}

const DocumentViewerModal: React.FC<DocumentViewerModalProps> = ({
  blob,
  fileName,
  contentType,
  onClose,
  onDownload,
}) => {
  const { t } = useLanguage();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [pdfDoc, setPdfDoc] = useState<any>(null);
  const [pageNum, setPageNum] = useState(1);
  const [numPages, setNumPages] = useState(0);
  const [imageUrl, setImageUrl] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  const isPdf = contentType.includes('pdf') || fileName.toLowerCase().endsWith('.pdf');
  const isImage = contentType.includes('image') || /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(fileName);

  useEffect(() => {
    if (!blob) return;
    setLoading(true);
    setError('');

    let cancelled = false;
    let createdImageUrl = '';

    (async () => {
      try {
        if (isPdf) {
          const buf = await blob.arrayBuffer();
          if (cancelled) return;
          const doc = await pdfjsLib.getDocument({ data: buf }).promise;
          if (cancelled) return;
          setPdfDoc(doc);
          setNumPages(doc.numPages);
          setPageNum(1);
        } else if (isImage) {
          createdImageUrl = URL.createObjectURL(blob);
          if (cancelled) return;
          setImageUrl(createdImageUrl);
        } else {
          // Translation key, not a literal — resolved through t() at render time so
          // switching language never re-runs this effect (which would re-parse the file).
          setError('dvmNoPreview');
        }
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'dvmLoadFailed');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      if (createdImageUrl) URL.revokeObjectURL(createdImageUrl);
    };
  }, [blob, isPdf, isImage]);

  useEffect(() => {
    if (!pdfDoc || !canvasRef.current) return;
    let cancelled = false;
    (async () => {
      try {
        const page = await pdfDoc.getPage(pageNum);
        if (cancelled) return;
        const canvas = canvasRef.current!;
        const containerWidth = Math.min(window.innerWidth - 24, 900);
        const unscaledViewport = page.getViewport({ scale: 1 });
        const scale = containerWidth / unscaledViewport.width;
        const viewport = page.getViewport({ scale });
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        await page.render({ canvasContext: ctx, viewport, canvas }).promise;
      } catch (e) {
        // ignore render errors (e.g., cancelled)
      }
    })();
    return () => { cancelled = true; };
  }, [pdfDoc, pageNum]);

  if (!blob) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: 'rgba(0,0,0,0.85)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 16px',
          background: '#111827',
          color: 'white',
        }}
      >
        <div style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 15, fontWeight: 600 }}>
          {fileName}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={onDownload}
            style={{
              background: '#D6B160',
              color: '#111827',
              border: 'none',
              borderRadius: 8,
              padding: '8px 12px',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              cursor: 'pointer',
            }}
          >
            <Download size={16} /> {t('dvmSave')}
          </button>
          <button
            onClick={onClose}
            aria-label={t('dvmClose')}
            style={{
              background: 'rgba(255,255,255,0.1)',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              padding: 8,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            <X size={18} />
          </button>
        </div>
      </div>

      <div style={{ flex: 1, overflow: 'auto', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', padding: 12 }}>
        {loading && (
          <div style={{ color: 'white', marginTop: 40, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Loader2 className="animate-spin" size={20} /> {t('dvmLoading')}
          </div>
        )}
        {!loading && error && (
          <div style={{ color: '#fca5a5', marginTop: 40, textAlign: 'center', padding: 20 }}>{t(error)}</div>
        )}
        {!loading && !error && isPdf && <canvas ref={canvasRef} style={{ maxWidth: '100%', height: 'auto', background: 'white', borderRadius: 4 }} />}
        {!loading && !error && isImage && imageUrl && (
          <img src={imageUrl} alt={fileName} style={{ maxWidth: '100%', height: 'auto', borderRadius: 4 }} />
        )}
      </div>

      {isPdf && numPages > 1 && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 12,
            padding: 10,
            background: '#111827',
            color: 'white',
          }}
        >
          <button
            onClick={() => setPageNum(p => Math.max(1, p - 1))}
            disabled={pageNum <= 1}
            style={{
              background: 'rgba(255,255,255,0.1)',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              padding: 8,
              display: 'flex',
              alignItems: 'center',
              cursor: pageNum <= 1 ? 'not-allowed' : 'pointer',
              opacity: pageNum <= 1 ? 0.4 : 1,
            }}
            aria-label={t('dvmPrevPage')}
          >
            <ChevronLeft size={18} />
          </button>
          <span style={{ fontSize: 14 }}>
            {t('dvmPage')} {pageNum} {t('dvmOf')} {numPages}
          </span>
          <button
            onClick={() => setPageNum(p => Math.min(numPages, p + 1))}
            disabled={pageNum >= numPages}
            style={{
              background: 'rgba(255,255,255,0.1)',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              padding: 8,
              display: 'flex',
              alignItems: 'center',
              cursor: pageNum >= numPages ? 'not-allowed' : 'pointer',
              opacity: pageNum >= numPages ? 0.4 : 1,
            }}
            aria-label={t('dvmNextPage')}
          >
            <ChevronRight size={18} />
          </button>
        </div>
      )}
    </div>
  );
};

export default DocumentViewerModal;
